import { useMemo } from 'react';
import { IconLoader2 } from '@tabler/icons-react';

/**
 *
 * @param Icon
 * @param {string} label
 * @param {boolean} isProcessing
 * @param {boolean} isDisabled
 * @param {"xs" |"sm"|"base" |"lg" |"xl" | "fixed"}size
 * @param {"primary" | "dark" | "gray" | "green" |"red" | "orange" | "alternative"} color
 * @param {boolean} outline
 * @param {"button" | "submit"} type
 * @param {boolean} iconOnly
 * @param {boolean} iconRight
 * @param props
 * @param {boolean }isTextNoWrap
 * @returns {JSX.Element}
 * @constructor
 */
const BButton = ({
  icon: Icon,
  label,
  isProcessing = false,
  isDisabled = false,
  size = 'base',
  color = 'primary',
  outline = false,
  type = 'button',
  isTextNoWrap = false,
  iconOnly = false,
  iconRight = false,
  ...props
}) => {
  const colorClassName = useMemo(() => {
    switch (color) {
      case 'primary':
        if (!outline)
          return 'bg-primary-500 text-white hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 disabled:opacity-50';
        return 'border border-primary-500 bg-white text-primary-500 hover:bg-primary-500 hover:text-white focus:bg-primary-700 focus:text-white focus:ring-4 focus:ring-primary-300 disabled:opacity-50';
      case 'dark':
        if (!outline)
          return 'bg-slate-800 text-white hover:bg-slate-600 focus:ring-4 focus:ring-slate-300 disabled:opacity-50';
        return 'border border-slate-600 bg-white text-slate-700 hover:bg-slate-600 hover:text-white focus:bg-slate-600 focus:text-white focus:ring-4 focus:ring-slate-300 disabled:opacity-50';
      case 'gray':
        if (!outline)
          return 'bg-slate-200 text-slate-800 hover:bg-slate-300 focus:ring-4 focus:ring-slate-100 disabled:opacity-50';
        return 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-200 hover:text-slate-700 focus:bg-slate-600 focus:text-slate-700 focus:ring-4 focus:ring-slate-100 disabled:opacity-50';
      case 'green':
        if (!outline)
          return 'bg-green-500 text-white hover:bg-green-700 focus:ring-4 focus:ring-green-200 disabled:opacity-50';
        return 'border border-green-500 bg-white text-green-500 hover:bg-green-500 hover:text-white focus:bg-green-700 focus:text-white focus:ring-4 focus:ring-green-200 disabled:opacity-50';
      case 'red':
        if (!outline)
          return 'bg-red-500 text-white hover:bg-red-700 focus:ring-4 focus:ring-red-200 disabled:opacity-50';
        return 'border border-red-500 bg-white text-red-500 hover:bg-red-500 hover:text-white focus:bg-red-700 focus:text-white focus:ring-4 focus:ring-red-200 disabled:opacity-50';
      case 'orange':
        if (!outline)
          return 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-4 focus:ring-orange-200 disabled:opacity-50';
        return 'border border-orange-600 bg-white text-orange-600 hover:bg-orange-600 hover:text-white focus:bg-orange-700 focus:text-white focus:ring-4 focus:ring-orange-200 disabled:opacity-50';
      case 'alternative':
        return 'border border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-primary-500 focus:bg-slate-100 focus:text-primary-500 focus:ring-4 focus:ring-slate-300 disabled:opacity-50';

      default:
        if (!outline)
          return 'bg-primary-500 text-white hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 disabled:opacity-50';
        return 'border border-primary-500 bg-white text-primary-500 hover:bg-primary-500 hover:text-white focus:bg-primary-700 focus:text-white focus:ring-4 focus:ring-primary-300 disabled:opacity-50';
    }
  }, [color, outline]);
  const sizeClassName = useMemo(() => {
    switch (size) {
      case 'xs':
        return {
          base: 'px-3 py-2 gap-2',
          label: 'text-xs font-medium',
          icon: 'size-4',
        };
      case 'sm':
        return {
          base: 'px-3 py-2 gap-2',
          label: 'text-sm font-medium',
          icon: 'size-4',
        };
      case 'base':
        return {
          base: 'px-5 py-2.5 gap-2',
          label: 'text-sm font-medium',
          icon: 'size-5',
        };
      case 'lg':
        return {
          base: 'px-5 py-3 gap-2',
          label: 'text-base font-medium',
          icon: 'size-5',
        };
      case 'xl':
        return {
          base: 'px-6 py-3.5 gap-2',
          label: 'text-base font-medium',
          icon: 'size-5',
        };
      case 'fixed':
        return {
          base: 'p-4.5 gap-2',
          label: 'text-base font-medium',
          icon: ' size-6',
        };
      default:
        return {
          base: 'px-5 py-2.5 gap-2',
          label: 'text-sm font-medium',
          icon: 'size-5',
        };
    }
  }, [size]);
  const roundedClassName = useMemo(() => {
    return iconOnly ? 'rounded-full' : 'rounded-lg';
  }, [iconOnly]);
  return (
    <button
      type={type}
      disabled={isDisabled}
      className={`${colorClassName} ${isDisabled ? 'pointer-events-none' : ''} ${sizeClassName.base} flex w-full items-center justify-center ${roundedClassName} transition-all duration-300 ease-in-out`}
      {...props}
    >
      {!iconRight && Icon && !isProcessing && <Icon className={sizeClassName.icon} />}
      {label && <p className={`${sizeClassName.label} ${isTextNoWrap ? 'text-nowrap' : ''}`}>{label}</p>}{' '}
      {isProcessing && <IconLoader2 className={`${sizeClassName.icon} animate-spin`} />}
      {iconRight && Icon && !isProcessing && <Icon className={sizeClassName.icon} />}
    </button>
  );
};

export default BButton;
